import clsx from 'clsx';
import { memo, useMemo, useRef } from 'react';

import type { SharedSideNavProps, SideNavItem, SideNavLink } from './types';

import {
  SIDE_NAV_COLLAPSED_WIDTH,
  SIDE_NAV_WIDTH,
} from '../../../../constants/sideNav';
import { useAppNavPresentation } from '../../../../context/AppNavPresentation/AppNavPresentation';
import { useNumberFormat } from '../../../../context/NumberFormat/NumberFormat';
import { Icon } from '../../../Icon/Icon';
import { AppNavBrandLink } from '../AppNavBrandLink';
import { SideNavContext, type SideNavContextValue } from './context';
import { useHasVerticalOverflow } from './hooks';
import { SideNavEntry } from './subcomponents/SideNavEntry';
import { SideNavPinnedSection } from './subcomponents/SideNavPinnedSection';
import { SideNavRailItem } from './subcomponents/SideNavRailItem';
import './SideNav.css';

const NO_ATTRIBUTES = {};
const NO_ITEMS: SideNavItem[] = [];
const NO_LINKS: SideNavLink[] = [];

/**
 * The application's primary navigation: a full sidebar, a narrow icon rail, and
 * the body of the drawer `AppLayout` shows on narrow viewports. It owns how the
 * navigation looks and behaves. The host owns what it lists, which entry is
 * current, and where pins are stored, and hears about presses through callbacks.
 */
export const SideNav = memo(function SideNav({
  activeKey,
  brand,
  collapsed = false,
  footer,
  footerItems = NO_ITEMS,
  items,
  labels,
  pinned = NO_LINKS,
  primaryItems = NO_ITEMS,
  regionAttributes = NO_ATTRIBUTES,
  onActivate,
  onReorderPinned,
  onToggleCollapsed,
  onTogglePin,
}: SharedSideNavProps) {
  const formatNumber = useNumberFormat();
  const { onClose, presentation } = useAppNavPresentation();
  const isDrawer = presentation === 'drawer';
  const isRail = collapsed && !isDrawer;
  const navigationRef = useRef<HTMLElement>(null);

  const isOverflowing = useHasVerticalOverflow(
    navigationRef,
    isRail,
    [items.length, pinned.length, primaryItems.length].join(','),
  );
  const isCondensed = isRail && isOverflowing;

  const pinnedKeys = useMemo(
    () => new Set(pinned.map((link) => link.key)),
    [pinned],
  );
  const hasPinControls = !isRail && onTogglePin !== undefined;
  const contextValue = useMemo<SideNavContextValue>(
    () => ({
      activeKey,
      labels,
      onActivate,
      pinControl:
        hasPinControls && onTogglePin
          ? {
              isPinned: (key) => pinnedKeys.has(key),
              onToggle: onTogglePin,
            }
          : undefined,
      regionAttributes,
    }),
    [
      activeKey,
      hasPinControls,
      labels,
      onActivate,
      onTogglePin,
      pinnedKeys,
      regionAttributes,
    ],
  );

  const toggleLabel = isRail ? labels.showSidebar : labels.hideSidebar;

  return (
    <SideNavContext.Provider value={contextValue}>
      <aside
        aria-label={labels.sidebar}
        className={clsx(
          'eds-side-nav',
          isRail && 'eds-side-nav--collapsed',
          isDrawer && 'eds-side-nav--drawer',
        )}
        style={
          isDrawer
            ? undefined
            : { width: isRail ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH }
        }
        {...regionAttributes.root}
      >
        {isDrawer && (
          <div
            className="eds-side-nav--drawer-header"
            {...regionAttributes.drawerHeader}
          >
            {brand && (
              <AppNavBrandLink brand={brand} className="eds-side-nav--brand" />
            )}
            <button
              type="button"
              aria-label={labels.hideSidebar}
              className="eds-side-nav--drawer-close"
              title={labels.hideSidebar}
              {...regionAttributes.drawerClose}
              onClick={onClose}
            >
              <Icon icon="x" size="s" />
            </button>
          </div>
        )}

        <div
          className={clsx(
            'eds-side-nav--header',
            isRail && 'eds-side-nav--header--collapsed',
          )}
          {...regionAttributes.header}
        >
          {!isRail && (
            <span className="eds-side-nav--overline">{labels.workspace}</span>
          )}
          {!isDrawer && onToggleCollapsed && (
            <button
              type="button"
              aria-label={toggleLabel}
              className="eds-side-nav--toggle"
              title={toggleLabel}
              {...regionAttributes.collapseToggle}
              onClick={onToggleCollapsed}
            >
              <Icon
                icon={isRail ? 'left-panel-open' : 'left-panel-close'}
                size="s"
              />
            </button>
          )}
        </div>

        <nav
          aria-label={labels.navigation}
          className={clsx(
            'eds-side-nav--scroll',
            isRail && 'eds-side-nav--scroll--collapsed',
            isCondensed && 'eds-side-nav--scroll--condensed',
          )}
          ref={navigationRef}
          {...regionAttributes.navigation}
        >
          {isRail && (
            <div
              aria-hidden="true"
              className="eds-side-nav--divider eds-side-nav--divider--collapsed"
            />
          )}
          {primaryItems.map((item) => (
            <SideNavEntry key={item.key} isRail={isRail} item={item} />
          ))}
          {primaryItems.length > 0 && (
            <div
              aria-hidden="true"
              className={clsx(
                'eds-side-nav--divider',
                isRail && 'eds-side-nav--divider--collapsed',
              )}
            />
          )}
          {isRail && pinned.length > 0 && (
            <>
              <SideNavRailItem
                item={{
                  children: pinned,
                  dataAttributes: regionAttributes.pinned,
                  icon: 'pin',
                  key: '__pinned__',
                  label: labels.pinned,
                  railBadge: formatNumber(pinned.length),
                }}
                neverCurrent
              />
              <div
                aria-hidden="true"
                className="eds-side-nav--divider eds-side-nav--divider--collapsed"
              />
            </>
          )}
          {!isRail && (
            <SideNavPinnedSection links={pinned} onReorder={onReorderPinned} />
          )}
          {items.map((item) => (
            <SideNavEntry key={item.key} isRail={isRail} item={item} />
          ))}
        </nav>

        <div
          className={clsx(
            'eds-side-nav--footer',
            isRail && 'eds-side-nav--footer--collapsed',
          )}
        >
          {footerItems.map((item) => (
            <SideNavEntry key={item.key} isRail={isRail} item={item} />
          ))}
          {footer}
        </div>
      </aside>
    </SideNavContext.Provider>
  );
});

SideNav.displayName = 'SideNav';
